import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  HStack,
  Radio,
  Text,
} from '@chakra-ui/react';
import Loading from '@components/Loading';
import ApiClient from '@config/network';
import { useCheckoutConfigContext } from '@contexts/CheckoutConfig';
import { DonateButtonProvider, useDonateButtonRef } from '@contexts/DonateButtonRef';
import { useStepper } from '@contexts/Stepper';
import {
  Checkout,
  CHECKOUT_STATE,
  DONATION_FREQUENCY,
  FormValues,
  ManualPM,
  PAYMENT_METHOD_TYPE,
  SatispayPM,
  STEP,
  STRIPE_PAYMENT_TYPE,
} from '@types';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import { Case, Switch, When } from 'react-if';
import CreditCardPanel from './CreditCardPanel';
import ManualPanel from './ManualPanel';
import PaypalPanel from './PaypalPanel';
import SatispayPanel from './SatispayPanel';
import SDDPanel from './SDDPanel';
import TestPanel from './TestPanel';
import { CHECKOUT_STATE_CHECK_TIMEOUT } from '@config/defaults';

const Payment: FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { watch, setValue, getValues } = useFormContext<FormValues>();
  const { checkout } = useCheckoutConfigContext();
  const { t } = useTranslation();

  const { isValidating, serverError } = useStepper();

  const paymentMethodId = watch('paymentMethodId');
  const frequency = watch('frequency');

  const handleComplete = async () => {
    setIsLoading(true);

    // checkout is manual, send complete
    if (getValues('paymentMethod') === PAYMENT_METHOD_TYPE.MANUAL) {
      await ApiClient.post(`rest/v1/checkout/${checkout.token}/complete`, {
        payment_method_id: paymentMethodId,
      });
    }

    // poll the checkout status until timeout or checkout is closed
    const startTime = Date.now();
    while (true) {
      try {
        const checkoutConfigRes = await ApiClient.get<Checkout>(
          `/rest/v1/checkout/${checkout.token}`,
        );

        if (checkoutConfigRes?.data?.state === CHECKOUT_STATE.CLOSED) {
          const thankyouUrl = `${checkout.campaign.sitefront_url}/thankyou/${checkout.token}`;
          if (inIframe()) {
            window.parent.postMessage(
              {
                type: 'checkoutCompleted',
                payload: {
                  token: checkout.token,
                  thankyouUrl,
                  supporterData: {
                    amount: checkout.amount,
                    frequency: checkout.frequency,
                    legalPerson: checkout.legal_person,
                    businessName: checkout.business_name,
                    firstName: checkout.first_name,
                    lastName: checkout.last_name,
                    email: checkout.email,
                  },
                },
              },
              '*',
            );
          } else {
            window.location.href = thankyouUrl;
          }

          break;
        }

        // check if timeout has been reached
        if (Date.now() - startTime > CHECKOUT_STATE_CHECK_TIMEOUT) {
          setError(t("We couldn't process your payment, please try again later"));
          console.error('Checkout state check timed out');
          break;
        } else {
          // wait for a while before checking again
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      } catch (err) {
        console.error(err);
        setError(t("We couldn't process your payment, please try again later"));
      }
    }

    setIsLoading(false);
  };

  if (isValidating) return <Loading />;

  return (
    <DonateButtonProvider>
      <Flex direction="column" h="full">
        {/* PAYMENT METHOD SECTION */}
        <Box w="full" pb={8}>
          <Text fontWeight="semibold" fontSize="xl" color="blackAlpha.800" w="full">
            {t('Payment')}
          </Text>
          <Text fontSize="md">{t('Choose your preferred payment method')}</Text>
        </Box>

        <When condition={checkout.enabled_payment_methods.length === 0}>
          <TestPanel formIsLoading={isLoading} onComplete={handleComplete} onError={setError} />
        </When>
        <When condition={checkout.enabled_payment_methods.length > 0}>
          <Accordion
            w="100%"
            borderRadius="lg"
            borderWidth={1}
            backgroundColor="white"
            onChange={(index: number) => {
              setValue('paymentMethod', checkout.enabled_payment_methods[index].type);
              setValue('paymentMethodId', checkout.enabled_payment_methods[index].id);
            }}
            index={checkout.enabled_payment_methods.findIndex(
              (method) => paymentMethodId == method.id,
            )}
          >
            {checkout.enabled_payment_methods
              .filter((method) => {
                if (
                  // filter out POS methods
                  ((method.type !== PAYMENT_METHOD_TYPE.STRIPE ||
                    method.stripe_payment_type !== STRIPE_PAYMENT_TYPE.POS) &&
                    // filter out methods that don't support the selected frequency
                    frequency === DONATION_FREQUENCY.ONEOFF &&
                    method.has_oneoff_support) ||
                  (frequency === DONATION_FREQUENCY.MONTHLY && method.has_subscription_support)
                )
                  return true;
              })
              .map((method) => (
                <AccordionItem
                  key={method.id}
                  w="100%"
                  borderBottomWidth={1}
                  borderTopWidth={0}
                  _last={{ borderBottomWidth: 0 }}
                >
                  <AccordionButton>
                    <Radio
                      colorScheme="primary"
                      sx={{ color: 'transparent !important' }}
                      isChecked={paymentMethodId === method.id}
                      mr={3}
                    />
                    <Text flexGrow={1} textAlign="left" size="md" fontWeight="semibold">
                      {method.label}
                    </Text>
                  </AccordionButton>

                  <AccordionPanel pb={4} overflow="auto">
                    <Switch>
                      <Case
                        condition={
                          method.type === PAYMENT_METHOD_TYPE.STRIPE &&
                          method.stripe_payment_type === STRIPE_PAYMENT_TYPE.CREDIT_CARD
                        }
                      >
                        <CreditCardPanel
                          formIsLoading={isLoading}
                          onComplete={handleComplete}
                          onError={setError}
                        />
                      </Case>
                      <Case
                        condition={
                          method.type === PAYMENT_METHOD_TYPE.STRIPE &&
                          method.stripe_payment_type === STRIPE_PAYMENT_TYPE.SEPA_DIRECT_DEBIT
                        }
                      >
                        <SDDPanel
                          formIsLoading={isLoading}
                          onComplete={handleComplete}
                          onError={setError}
                        />
                      </Case>
                      <Case condition={method.type === PAYMENT_METHOD_TYPE.PAYPAL}>
                        <PaypalPanel onComplete={handleComplete} onError={setError} />
                      </Case>
                      <Case condition={method.type == PAYMENT_METHOD_TYPE.SATISPAY}>
                        <SatispayPanel
                          onComplete={handleComplete}
                          onError={setError}
                          method={method as SatispayPM}
                        />
                      </Case>
                      <Case condition={method.type === PAYMENT_METHOD_TYPE.MANUAL}>
                        <ManualPanel
                          method={method as ManualPM}
                          formIsLoading={isLoading}
                          onComplete={handleComplete}
                          onError={setError}
                        />
                      </Case>
                    </Switch>
                  </AccordionPanel>
                </AccordionItem>
              ))}
          </Accordion>
        </When>
        <Box flexGrow={1} minH={4} />
        <When condition={!!error || serverError}>
          <Text color="red.500" fontSize="sm" textAlign="center" py={4}>
            {error}
          </Text>
        </When>

        <DonateButtonPortal />
      </Flex>
    </DonateButtonProvider>
  );
};

const DonateButtonPortal: FC = () => {
  const { t } = useTranslation();
  const { goToStep, isValidating } = useStepper();
  const { ref } = useDonateButtonRef();

  return (
    <HStack pt={4} spacing={2} justify="space-between" alignItems="center">
      <Button
        size="lg"
        onClick={() => goToStep(STEP.SUPPORTER, true)}
        disabled={isValidating}
        variant="link"
        fontSize={['sm', 'md']}
        leftIcon={<HiOutlineArrowLeft />}
        _hover={{ backgroundColor: 'transparent', textDecoration: 'underline' }}
      >
        {t('Back to details')}
      </Button>

      <Box ref={ref} />
    </HStack>
  );
};

function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

export default Payment;
